/**
 * Page Profil Unifiee
 * Fusionne Profil Accord Cadre et Profil Prestataire Libre
 * L'affichage s'adapte selon le role de l'utilisateur connecte
 * 
 * FRONT-08: Meme interface pour PL et AC, avec des onglets adaptes
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProfileProvider, useProfile } from '../../contexts/ProfileContext';
import { useAuth } from '../../contexts/AuthContext';
import TabNav from '../../composants/TabNav/TabNav';
import ProfilSkeleton from '../../composants/Skeleton/ProfilSkeleton';
import Button from '../../composants/Button/Button';
import { useProfilType } from './hooks/useProfilType';

// Onglets communs PL + AC
import TabPresentation from './tabs/TabPresentation';
import TabDocuments from './tabs/TabDocuments';
import TabReferences from './tabs/TabReferences';
import TabCoordonnees from './tabs/TabCoordonnees';
import TabBancaire from './tabs/TabBancaire';
import TabEquipe from './tabs/TabEquipe';

// Composants specifiques
import ProgressionBar from './components/ProgressionBar';
import AlertMessage from './components/AlertMessage';
import EvaluationCentralis, { EvaluationCentralisData, EvalCritere, EvaluationLastUpdateBanner } from '../../composants/EvaluationCentralis/EvaluationCentralis';
import { pdf } from '@react-pdf/renderer';
import AvisPDF from '../PDF/AvisPDF';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import SectionPliable from '../../composants/SectionPliable/SectionPliable';
import Banner from '../../composants/Banner/Banner';
import AvisGroupes from '../../composants/AvisPrestataire/AvisGroupes';
import { AvisData } from '../../composants/AvisPrestataire/avisTypes';
import { apiService } from '../../services/apiService';

import './ProfilUnifie.css';
import './ProfilFormHarmonise.css';

/**
 * Contenu du profil unifie (utilise ProfileContext)
 * FRONT-08: Fonctionne pour PL ET AC
 */
const ProfilUnifieContent: React.FC = () => {
  const {
    profile,
    completion,
    isLoading,
    isSaving,
    error,
    successMessage,
    loadProfile,
    clearMessages,
    hasPendingChanges,
  } = useProfile();

  const profilType = useProfilType();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [showNavGuard, setShowNavGuard] = useState(false);
  const pendingHrefRef = useRef<string | null>(null);
  const [evaluationCentralis, setEvaluationCentralis] = useState<EvaluationCentralisData | null>(null);
  const [evaluationCentralisLoading, setEvaluationCentralisLoading] = useState<boolean>(false);
  // Avis recus par ce prestataire de la part des acheteurs commanditaires.
  const [avisRecus, setAvisRecus] = useState<AvisData[]>([]);
  const [avisMoyenne, setAvisMoyenne] = useState<number | null>(null);
  const [avisRecusLoaded, setAvisRecusLoaded] = useState<boolean>(false);
  // Avis DONNES par ce prestataire aux acheteurs (PRIVE : 2 parties seules).
  const [avisAcheteurDonnes, setAvisAcheteurDonnes] = useState<AvisData[]>([]);
  const [avisAcheteurDonnesLoaded, setAvisAcheteurDonnesLoaded] = useState<boolean>(false);

  // Bloquer la fermeture/rechargement du navigateur
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasPendingChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingChanges]);

  // Intercepter les clics sur les liens de navigation SPA
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!hasPendingChanges) return;
      const anchor = (e.target as Element).closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('http')) return;
      // Lien interne — intercepter
      e.preventDefault();
      e.stopPropagation();
      pendingHrefRef.current = href;
      setShowNavGuard(true);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasPendingChanges]);

  // FRONT-08: Onglets adaptes selon le type de profil
  // Prestataire Libre : tous les onglets avec References
  // Accord Cadre : onglets essentiels (pas de References)
  const TABS_PL = [
    'Présentation',
    'Documents',
    'Références',
    'Bancaire',
    'Gestion d\'équipe',
    'Évaluations',
  ];

  const TABS_AC = [
    'Présentation',
    'Documents',
    'Références',
    'Bancaire',
    'Gestion d\'équipe',
    'Évaluations',
  ];

  // Selection des onglets selon le type
  const tabs = profilType.isPrestataireLibre ? TABS_PL : TABS_AC;

  // Synchronise l'onglet actif avec l'URL (?tab=) a CHAQUE changement, pas seulement au montage :
  // on reste sur l'onglet au rechargement ET le menu deroulant (liens /profil?tab=X) change bien
  // d'onglet quand on est deja sur /profil. Idempotent : si ?tab= == onglet courant, setActiveTab est un no-op.
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (!urlTab) return;
    const idx = tabs.indexOf(urlTab);
    if (idx !== -1) setActiveTab(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Change d'onglet + persiste dans l'URL (?tab=).
  const handleTabChange = useCallback((idx: number) => {
    setActiveTab(idx);
    const name = tabs[idx];
    if (name) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', name);
      setSearchParams(next, { replace: true });
    }
  }, [tabs, searchParams, setSearchParams]);

  // Charger le profil au montage
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Charge l'evaluation Centralis a l'ouverture de l'onglet "Évaluations" (cote prestataire).
  // IMPORTANT : ne PAS mettre evaluationCentralisLoading dans les deps -> sinon setLoading(true)
  // re-declenche l'effet, son cleanup passe cancelled=true sur le fetch en vol, et le .finally
  // ne remet jamais loading a false (skeleton infini). Meme schema que Profil.tsx (acheteur).
  useEffect(() => {
    if (tabs[activeTab] !== 'Évaluations' || evaluationCentralis) {
      return;
    }
    let cancelled = false;
    setEvaluationCentralisLoading(true);
    apiService.getEvaluationCentralis()
      .then((data: EvaluationCentralisData | null) => {
        if (!cancelled) setEvaluationCentralis(data);
      })
      .catch(() => {
        if (!cancelled) setEvaluationCentralis(null);
      })
      .finally(() => {
        if (!cancelled) setEvaluationCentralisLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, evaluationCentralis]);

  // Avis recus : effet SEPARE (son propre `cancelled`). Couple au fetch centralis, le changement
  // d'etat `evaluationCentralis` re-declenchait l'effet et annulait ce fetch en vol (reponse recue
  // mais setState ignore -> liste vide). Decouple = plus de course.
  useEffect(() => {
    if (tabs[activeTab] !== 'Évaluations' || avisRecusLoaded) {
      return;
    }
    let cancelled = false;
    apiService.getAvisPrestataireRecus()
      .then((res: { avis?: AvisData[]; moyenne?: number | null } | null) => {
        if (!cancelled) {
          setAvisRecus(Array.isArray(res?.avis) ? (res!.avis as AvisData[]) : []);
          setAvisMoyenne(res?.moyenne ?? null);
          setAvisRecusLoaded(true);
        }
      })
      .catch(() => { if (!cancelled) setAvisRecusLoaded(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, avisRecusLoaded]);

  // Avis DONNES aux acheteurs (PRIVE) : effet separe, meme onglet Évaluations.
  useEffect(() => {
    if (tabs[activeTab] !== 'Évaluations' || avisAcheteurDonnesLoaded) {
      return;
    }
    let cancelled = false;
    apiService.getAvisAcheteurDonnes()
      .then((d: AvisData[]) => {
        if (!cancelled) { setAvisAcheteurDonnes(Array.isArray(d) ? d : []); setAvisAcheteurDonnesLoaded(true); }
      })
      .catch(() => { if (!cancelled) setAvisAcheteurDonnesLoaded(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, avisAcheteurDonnesLoaded]);

  // Clic d'etoile : mise a jour optimiste de mes notes, puis upsert serveur (autoritaire).
  const handleRateCentralis = useCallback((critere: EvalCritere, note: number) => {
    setEvaluationCentralis((prev) =>
      prev ? { ...prev, notes: { ...prev.notes, [critere]: note } } : prev
    );
    apiService.rateEvaluationCentralis(critere, note)
      .then((data: EvaluationCentralisData | null) => {
        if (data) setEvaluationCentralis(data);
      })
      .catch(() => {
        apiService.getEvaluationCentralis()
          .then((data: EvaluationCentralisData | null) => setEvaluationCentralis(data))
          .catch(() => undefined);
      });
  }, []);

  // Export PDF des avis recus par l'entite de ce prestataire (section "Avis des acheteurs commanditaires").
  const handleExportAvisPdf = useCallback(async () => {
    try {
      const blob = await pdf(
        <AvisPDF
          avis={avisRecus}
          cote="prestataire"
          generatedAt={new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' })}
          generatedBy={user?.name || user?.email || '—'}
        />,
      ).toBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avis-recus-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Une erreur est survenue lors de la génération du PDF des avis.');
    }
  }, [avisRecus, user]);

  // Export PDF des avis DONNES aux acheteurs (section privee "Avis que vous avez donnés aux
  // acheteurs"). Meme generateur, sens "donnes" (titres adaptes).
  const handleExportAvisAcheteurPdf = useCallback(async () => {
    try {
      const blob = await pdf(
        <AvisPDF
          avis={avisAcheteurDonnes}
          cote="prestataire"
          sens="donnes"
          generatedAt={new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' })}
          generatedBy={user?.name || user?.email || '—'}
        />,
      ).toBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avis-donnes-acheteurs-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Une erreur est survenue lors de la génération du PDF des avis.');
    }
  }, [avisAcheteurDonnes, user]);

  // Determiner quels onglets ont des problemes (non completes)
  // CORE-15: AC voit aussi les indicateurs de completion comme PL
  // FRONT-12: References a l'index 2, Bancaire (avec Coordonnees) a l'index 3
  const getTabsWithIssues = useCallback((): number[] => {
    if (!completion?.sections) return [];

    const issues: number[] = [];

    Object.entries(completion.sections).forEach(([name, complete]) => {
      if (!complete) {
        switch (name) {
          case 'presentation':
            issues.push(0);
            break;
          case 'documents':
            issues.push(1);
            break;
          case 'references':
            issues.push(2);
            break;
          case 'coordonnees':
            // FRONT-11: Coordonnees fusionne dans Bancaire (index 3)
            issues.push(3);
            break;
          case 'bancaire':
            issues.push(3);
            break;
        }
      }
    });

    // Retirer les doublons
    return Array.from(new Set(issues));
  }, [completion]);

  // FRONT-08 + FRONT-21: Rendu du contenu selon l'onglet actif
  // FRONT-21: TabDocuments détecte automatiquement le rôle (AC ou PL) sans prop
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <TabPresentation />;
      case 1:
        return <TabDocuments />;
      case 2:
        return <TabReferences />;
      case 3:
        return <TabBancaire />;
      case 4:
        return <TabEquipe />;
      case 5:
        return (
          <>
            <EvaluationCentralis
              cote="prestataire"
              data={evaluationCentralis}
              loading={evaluationCentralisLoading}
              onRate={handleRateCentralis}
            />
            <div className="profil-tab-page evaluations-page">
              <SectionPliable title="Avis des acheteurs concernant les prestataires">
                <Banner
                  variant="info"
                  className="eval-notice"
                  message="Par défaut, il vous est attribué automatiquement la note maximale lors de la commande. Mais l'acheteur peut modifier cette note et l'avis associé à tout moment, en fonction de votre capacité à mener à bien l'opération. Ces informations sont visibles publiquement."
                />
                <AvisGroupes
                  avis={avisRecus}
                  groupBy="acheteur"
                  average={avisMoyenne}
                  loading={!avisRecusLoaded}
                  emptyMessage="Aucun avis pour le moment. Les acheteurs déposent leurs avis depuis leurs commandes validées."
                />
                {/* Export PDF de l'ensemble des avis recus (bas a droite de la section). */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.2rem' }}>
                  <Button
                    variant="secondary"
                    value="Télécharger les avis reçus"
                    icon={<PdfIcon />}
                    onClick={handleExportAvisPdf}
                    disabled={!avisRecusLoaded || avisRecus.length === 0}
                  />
                </div>
              </SectionPliable>
              <SectionPliable title="Avis que vous avez donnés aux acheteurs">
                <Banner
                  variant="info"
                  className="eval-notice"
                  message="Par défaut, vous attribuez automatiquement la note maximale à l'acheteur lors de la commande. Vous pouvez la modifier à tout moment depuis l'onglet « Évaluation » de la commande. Ces informations restent privées : seuls vous et l'acheteur concerné y ont accès."
                />
                <AvisGroupes
                  avis={avisAcheteurDonnes}
                  groupBy="acheteur"
                  loading={!avisAcheteurDonnesLoaded}
                  emptyMessage="Vous n'avez encore évalué aucun acheteur. Les avis se déposent depuis l'onglet « Évaluation » d'une commande validée."
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.2rem' }}>
                  <Button
                    variant="secondary"
                    value="Télécharger les avis donnés"
                    icon={<PdfIcon />}
                    onClick={handleExportAvisAcheteurPdf}
                    disabled={!avisAcheteurDonnesLoaded || avisAcheteurDonnes.length === 0}
                  />
                </div>
              </SectionPliable>
              {/* Bandeau vert "Derniere mise a jour", a la TOUTE FIN de la page. */}
              <EvaluationLastUpdateBanner data={evaluationCentralis} />
            </div>
          </>
        );
      default:
        return <TabPresentation />;
    }
  };

  // Etat de chargement initial : skeleton (en-tete + onglets + sections)
  if (isLoading && !profile) {
    return (
      <div className="profil-container">
        <ProfilSkeleton header tabs sections={2} />
      </div>
    );
  }

  // Erreur de chargement
  if (!isLoading && !profile && error) {
    return (
      <div className="profil-container">
        <div className="error-container">
          <h2>Erreur de chargement</h2>
          <p>{error}</p>
          <Button value="Reessayer" onClick={loadProfile} />
        </div>
      </div>
    );
  }

  return (
    <div className="profil-container">
      {/* Modal de confirmation de navigation si modifications en attente */}
      {showNavGuard && (
        <div className="profil-nav-guard-overlay">
          <div className="profil-nav-guard-modal">
            <div className="profil-nav-guard-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3>Modifications en cours</h3>
            <p>Des modifications n'ont pas encore été enregistrées. Êtes-vous sûr de vouloir quitter&nbsp;?</p>
            <div className="profil-nav-guard-actions">
              <Button
                variant="secondary"
                value="Rester sur la page"
                onClick={() => {
                  setShowNavGuard(false);
                  pendingHrefRef.current = null;
                }}
              />
              <Button
                variant="danger"
                value="Quitter sans sauvegarder"
                onClick={() => {
                  const href = pendingHrefRef.current;
                  setShowNavGuard(false);
                  pendingHrefRef.current = null;
                  if (href) window.location.href = href;
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Popup quotidienne si profil incomplet — désactivée ici, gérée globalement dans MainLayout */}

      {/* Messages d'alerte */}
      {(error || successMessage) && (
        <AlertMessage
          type={error ? 'error' : 'success'}
          message={error || successMessage || ''}
          onClose={clearMessages}
        />
      )}

      {/* Navigation par onglets ; progression (% + pastille + barre) a droite de la ligne d'onglets,
          plus de gros titre "Profil" en en-tete. */}
      <div className="profil-tabs">
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          notifications={getTabsWithIssues().map(idx => ({ tabIndex: idx, count: 1 }))}
          rightContent={completion && (() => {
            // Statut couleur : vert si complet, orange si entame (>0), rouge si a 0.
            const pct = completion.percent ?? 0;
            const statutClass = completion.is_complete ? 'is-complete' : pct > 0 ? 'is-partial' : 'is-incomplete';
            const barColor = completion.is_complete ? '#22c55e' : pct > 0 ? '#f97316' : '#ef4444';
            return (
              <div className="profil-header-completion">
                <div className="profil-header-completion-top">
                  <span className={`profil-header-percent ${statutClass}`}>{pct}%</span>
                  <span className={`profil-header-pastille ${statutClass}`}>
                    {completion.is_complete ? 'Complet' : 'Incomplet'}
                  </span>
                </div>
                <div className="profil-header-bar">
                  <div
                    className="profil-header-bar-fill"
                    style={{ width: `${pct}%`, backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })()}
        />
      </div>

      {/* Contenu de l'onglet */}
      <div className="profil-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

/**
 * Composant principal unifie
 * FRONT-08: Meme wrapper pour PL ET AC
 * Utilise ProfileProvider pour les deux types
 */
const ProfilUnifie: React.FC = () => {
  return (
    <ProfileProvider>
      <ProfilUnifieContent />
    </ProfileProvider>
  );
};

export default ProfilUnifie;
